'use client';

import { useState, useMemo, useEffect } from 'react';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { getBaseColor, OTHER_COLOR_HEX } from '@/lib/baseColors';
import { resolveColorNameToBase } from '@/lib/colorResolverCore';
import { type ProductVariantOption } from '@/lib/productService';
import {
  getVariantAttributeFallbackLabel,
  isSelectableVariantAttribute,
  SELECTABLE_VARIANT_ATTRIBUTES,
} from '@/lib/variantDetails';

const MULTICOLOR_GRADIENT =
  'conic-gradient(#ef4444,#f59e0b,#facc15,#22c55e,#3b82f6,#a855f7,#ef4444)';

/**
 * HEX swatch (bolinha) for a color option. Prefers the store's canonical mapping
 * (`baseColorId` cached on the variant) so a mapped tone like "Branco Gelo" → Branco
 * shows the registered swatch; only falls back to the pure name resolver when unmapped.
 */
function ColorSwatch({ rawColor, baseColorId }: { rawColor: string; baseColorId?: string }) {
  const base =
    getBaseColor(baseColorId) ?? getBaseColor(resolveColorNameToBase(rawColor).baseColorId);
  const style: React.CSSProperties = base?.gradient
    ? { backgroundImage: MULTICOLOR_GRADIENT }
    : { backgroundColor: base?.hex ?? OTHER_COLOR_HEX };
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 shrink-0 rounded-full border border-black/15 dark:border-white/25"
      style={style}
    />
  );
}

type FlexibleVariantSelectorProps = {
  variants: ProductVariantOption[];
  onVariantChange: (variant: ProductVariantOption | null) => void;
  onSelectionsChange?: (selections: Record<string, string>) => void;
  unlimitedStock?: boolean;
  defaultSelections?: Record<string, string>;
};

export { SELECTABLE_VARIANT_ATTRIBUTES };

const VARIANT_ATTRIBUTE_ORDER: Record<string, number> = {
  garmentType: 0,
  size: 1,
  color: 2,
  material: 3,
  fit: 4,
  style: 5,
  pattern: 6,
  gender: 7,
  length: 8,
  width: 9,
  neckSize: 10,
  waist: 11,
  dosage: 12,
  form: 13,
  concentration: 14,
  flavor: 15,
  packSize: 16,
};

export function getSelectableVariantAttributeNames(
  variants: ProductVariantOption[]
): string[] {
  const names = new Set<string>();

  variants.forEach((variant) => {
    Object.keys(variant).forEach((key) => {
      if (isSelectableVariantAttribute(key)) {
        names.add(key);
      }
    });
  });

  return Array.from(names).sort(
    (a, b) => (VARIANT_ATTRIBUTE_ORDER[a] ?? 99) - (VARIANT_ATTRIBUTE_ORDER[b] ?? 99)
  );
}

/**
 * Flexible Variant Selector that works with ANY attributes!
 * 
 * This component automatically detects all attributes present in variants
 * and creates selectors for each one.
 * 
 * Examples it handles:
 * - { size: "M", color: "Preto", stock: 5 }
 * - { storage: "128GB", color: "Preto", stock: 10 }
 * - { weight: "500g", type: "Orgânico", stock: 20 }
 */
export function FlexibleVariantSelector({
  variants,
  onVariantChange,
  onSelectionsChange,
  unlimitedStock = false,
  defaultSelections,
}: FlexibleVariantSelectorProps) {
  const t = useTranslations('product');
  const [selections, setSelections] = useState<Record<string, string>>(
    () => defaultSelections ?? {}
  );

  useEffect(() => {
    setSelections(defaultSelections ?? {});
  }, [defaultSelections]);

  // Get all attribute names from variants (only include selectable attributes)
  const attributeNames = useMemo(() => {
    return getSelectableVariantAttributeNames(variants);
  }, [variants]);

  const valuesByAttribute = useMemo(() => {
    const map: Record<string, string[]> = {};

    attributeNames.forEach(attributeName => {
      const values: string[] = [];
      const seen = new Set<string>();

      variants.forEach(variant => {
        const value = (variant as any)[attributeName];
        if (value !== undefined && value !== null) {
          const stringValue = String(value);
          if (!seen.has(stringValue)) {
            seen.add(stringValue);
            values.push(stringValue);
          }
        }
      });

      map[attributeName] = values;
    });

    return map;
  }, [attributeNames, variants]);

  // Canonical base color per raw color value, for the swatch (respects the store mapping).
  const colorBaseByValue = useMemo(() => {
    const map = new Map<string, string>();
    variants.forEach((variant) => {
      if (variant.color && variant.colorBaseId && !map.has(variant.color)) {
        map.set(variant.color, variant.colorBaseId);
      }
    });
    return map;
  }, [variants]);

  const matchesSelections = (
    variant: ProductVariantOption,
    testSelections: Record<string, string>
  ) => {
    return Object.entries(testSelections).every(([key, val]) => {
      const variantValue = (variant as any)[key];
      return variantValue !== undefined && String(variantValue) === val;
    });
  };

  // Check if a specific value is available (has stock)
  const isValueAvailable = (attributeName: string, value: string): boolean => {
    const testSelections = { ...selections, [attributeName]: value };
    return variants.some(v =>
      matchesSelections(v, testSelections) && (unlimitedStock || v.stock > 0)
    );
  };

  // Get currently selected variant
  const selectedVariant = useMemo(() => {
    // Must have selection for all attributes
    if (attributeNames.length === 0 || Object.keys(selections).length !== attributeNames.length) {
      return null;
    }

    return variants.find(v => matchesSelections(v, selections)) || null;
  }, [variants, selections, attributeNames]);

  // Update selectedVariant when selections change (use useEffect to avoid render-time updates)
  useEffect(() => {
    onVariantChange(selectedVariant);
  }, [selectedVariant, onVariantChange]);

  useEffect(() => {
    onSelectionsChange?.(selections);
  }, [onSelectionsChange, selections]);

  const getAttributeLabel = (attributeName: string) => {
    const messageKey = `attributeLabels.${attributeName}`;
    return t.has(messageKey)
      ? t(messageKey)
      : getVariantAttributeFallbackLabel(attributeName);
  };

  const handleSelection = (attributeName: string, value: string) => {
    const newSelections = { ...selections, [attributeName]: value };
    const hasMatch = (testSelections: Record<string, string>) =>
      variants.some(v => matchesSelections(v, testSelections));

    if (!hasMatch(newSelections)) {
      const attributesToCheck = attributeNames.filter(
        (attr) => attr !== attributeName && newSelections[attr]
      );

      for (let i = attributesToCheck.length - 1; i >= 0; i -= 1) {
        if (hasMatch(newSelections)) break;
        const attr = attributesToCheck[i];
        delete newSelections[attr];
      }
    }

    setSelections(newSelections);
  };

  return (
    <div className="space-y-6">
      {/* Dynamic Attribute Selectors */}
      {attributeNames.map((attributeName) => {
        const availableValues = valuesByAttribute[attributeName] || [];

        if (availableValues.length === 0) return null;

        return (
          <div key={attributeName} className="space-y-2">
            <label className="text-sm font-semibold">{getAttributeLabel(attributeName)}:</label>
            <div className="flex flex-wrap gap-2">
              {availableValues.map((value) => {
                const isSelected = selections[attributeName] === value;
                const isAvailable = isValueAvailable(attributeName, value);

                return (
                  <Button
                    key={value}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleSelection(attributeName, value)}
                    aria-disabled={!isAvailable}
                    className={`min-w-[60px] rounded-full ${!isAvailable && !isSelected ? 'opacity-50' : ''}`}
                  >
                    {attributeName === 'color' ? (
                      <ColorSwatch rawColor={value} baseColorId={colorBaseByValue.get(value)} />
                    ) : null}
                    {value}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
